import { FreshRecipe } from "@api-clients/subscriptions";

import { Allergen } from "@/primary-models";

export type TTexture = "chunky" | "smooth" | "shredded";

export interface IFreshRecipeDetails {
  name: string;
  description: string;
  imageSrc: string;
  allergens: string[];
  forKittens: boolean;
  texture: TTexture;
}

export type FreshRecipeDetailsMap = Record<FreshRecipe, IFreshRecipeDetails>;

export const IMAGE_BASE_URL = "/images/features/product-overview/packaging/";

const ALL_FRESH_RECIPE_DETAILS: FreshRecipeDetailsMap = {
  CLUCK: {
    name: "CLUCK",
    description: "Fresh, 100% gently-cooked chicken thigh, liver and heart.",
    imageSrc: "cluck_2025.webp",
    allergens: [ Allergen.CHICKEN ],
    forKittens: true,
    texture: "chunky",
  },
  GOBBLE: {
    name: "GOBBLE",
    description: "Fresh, 100% gently-cooked turkey thigh, liver and chicken thigh and gizzards.",
    imageSrc: "gobble_2026.webp",
    allergens: [ Allergen.TURKEY, Allergen.CHICKEN ],
    forKittens: true,
    texture: "chunky",
  },
  QUACK: {
    name: "QUACK",
    description: "Fresh, 100% gently-cooked chicken heart, thigh, liver and duck gizzards.",
    imageSrc: "quack_2025.webp",
    allergens: [ Allergen.DUCK, Allergen.CHICKEN, Allergen.SALMON ],
    forKittens: true,
    texture: "chunky",
  },
  MOOO: {
    name: "MOOO",
    description: "Fresh, 100% gently-cooked beef trim, heart and liver.",
    imageSrc: "mooo_2026.webp",
    allergens: [ Allergen.BEEF, Allergen.SALMON ],
    forKittens: true,
    texture: "chunky",
  },
  OINK: {
    name: "OINK",
    description: "Fresh, 100% gently-cooked pork shoulder, trim and liver.",
    imageSrc: "oink_2025.webp",
    allergens: [ Allergen.PORK, Allergen.SALMON ],
    forKittens: true,
    texture: "chunky",
  },
  BAAA: {
    name: "BAAA",
    description: "Fresh, 100% gently-cooked lamb heart, trim, liver and kidney.",
    imageSrc: "baaa_2025.webp",
    allergens: [ Allergen.LAMB, Allergen.BEEF, Allergen.SALMON ],
    forKittens: false,
    texture: "chunky",
  },
  SWISH: {
    name: "SWISH",
    description: "Fresh, 100% gently-cooked tuna, salmon, chicken heart and liver.",
    imageSrc: "swish_2026.webp",
    allergens: [ Allergen.TUNA, Allergen.SALMON, Allergen.CHICKEN ],
    forKittens: true,
    texture: "chunky",
  },
  SPLASH: {
    name: "SPLASH",
    description: "Fresh, 100% gently-cooked whitefish, salmon and turkey liver.",
    imageSrc: "splash_2025.webp",
    allergens: [ Allergen.WHITEFISH, Allergen.TURKEY, Allergen.SALMON ],
    forKittens: true,
    texture: "chunky",
  },
  SHREDDED_CLUCK: {
    name: "CLUCK (SHREDDED)",
    description: "Fresh, 100% gently-cooked shredded chicken thigh, liver and heart.",
    imageSrc: "shredded-cluck.webp",
    allergens: [ Allergen.CHICKEN, Allergen.SALMON ],
    forKittens: true,
    texture: "shredded",
  },
  SHREDDED_GOBBLE: {
    name: "GOBBLE (SHREDDED)",
    description: "Fresh, 100% gently-cooked shredded turkey thigh, liver and chicken thigh, gizzards.",
    imageSrc: "shredded-gobble.webp",
    allergens: [ Allergen.TURKEY, Allergen.CHICKEN, Allergen.SALMON ],
    forKittens: true,
    texture: "shredded",
  },
  SMOOTH_CLUCK: {
    name: "CLUCK (SMOOTH)",
    description: "Fresh, 100% gently-cooked chicken thigh, liver and heart.",
    imageSrc: "smooth_cluck_2026.webp",
    allergens: [ Allergen.CHICKEN ],
    forKittens: true,
    texture: "smooth",
  },
  SMOOTH_PECK: {
    name: "PECK (SMOOTH)",
    description: "Fresh, 100% gently-cooked chicken liver and heart, turkey thigh and duck gizzards.",
    imageSrc: "smooth_peck_2026.webp",
    allergens: [ Allergen.CHICKEN, Allergen.TURKEY, Allergen.DUCK ],
    forKittens: true,
    texture: "smooth",
  },
};

export const getFreshRecipeDetails = (isShreddedEnabled: boolean): Record<string, IFreshRecipeDetails> => {
  if (isShreddedEnabled) {
    return ALL_FRESH_RECIPE_DETAILS;
  }

  const {
    SHREDDED_CLUCK: _shreddedCluck,
    SHREDDED_GOBBLE: _shreddedGobble,
    ...nonShreddedRecipes
  } = ALL_FRESH_RECIPE_DETAILS;

  return nonShreddedRecipes;
};

export const getFreshRecipeDisplayName = (recipe: string, fallbackName?: string): string => (
  ALL_FRESH_RECIPE_DETAILS[recipe as FreshRecipe]?.name ?? fallbackName ?? recipe
);
